import React, { useMemo, useState } from 'react';
import { contentApi } from '../../services/contentApi';
import { CourseEnrollment, CourseInterviewPackageConfig } from '../../types/content';
import Button from '../UI/Button';

type Props = {
  courseId: number;
  enrollment: CourseEnrollment;
  packages: CourseInterviewPackageConfig | null;
  onUpgraded: (enrollment: CourseEnrollment, message?: string) => void;
};

/** Upgrade purchased course review sessions (pay only the package fee difference). */
const CourseInterviewUpgrade: React.FC<Props> = ({ courseId, enrollment, packages, onUpgraded }) => {
  const current = enrollment.interviewPackageCount || 0;
  const maxOffered = Math.max(
    ...(packages?.packages || []).map((p) => p.count),
    packages?.maxSessions || 3,
    3
  );
  const tiers = useMemo(
    () => (packages?.packages || []).filter((p) => p.count > current && p.count <= maxOffered),
    [packages, current, maxOffered]
  );
  const [target, setTarget] = useState(tiers[0]?.count || 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!enrollment.isPaid || tiers.length === 0) return null;

  const currentFee = Number(enrollment.interviewPackageFee || 0);
  const selected = tiers.find((t) => t.count === target) || tiers[0];
  const delta = Math.max(0, Number(selected?.totalPrice || 0) - currentFee);

  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-indigo-800">Upgrade review sessions</p>
      <p className="text-sm text-indigo-900/90">
        You have {current} session{current === 1 ? '' : 's'}. Upgrade to more professional reviews — pay only the difference.
      </p>
      <fieldset className="space-y-2">
        {tiers.map((tier) => {
          const tierDelta = Math.max(0, tier.totalPrice - currentFee);
          return (
            <label key={tier.count} className="flex items-start gap-2 text-sm cursor-pointer text-indigo-950">
              <input
                type="radio"
                name={`upgrade-pkg-${courseId}`}
                className="mt-1"
                checked={target === tier.count}
                onChange={() => setTarget(tier.count)}
              />
              <span>
                {tier.count} sessions — ₹{tier.totalPrice.toLocaleString('en-IN')} total
                <span className="text-indigo-700"> (pay ₹{tierDelta.toLocaleString('en-IN')} more)</span>
              </span>
            </label>
          );
        })}
      </fieldset>
      {error && <p className="text-sm text-error-600">{error}</p>}
      <Button
        type="button"
        size="sm"
        disabled={busy || !target}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            const response = await contentApi.upgradeCourseInterviews(courseId, target);
            if (!response.data.success || !response.data.enrollment) {
              setError(response.data.message || 'Upgrade failed.');
              return;
            }
            onUpgraded(response.data.enrollment, response.data.message);
          } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Upgrade failed.');
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Upgrading…' : `Upgrade · ₹${delta.toLocaleString('en-IN')}`}
      </Button>
    </div>
  );
};

export default CourseInterviewUpgrade;
